/**
 * One review: the summary on top, the document with every flagged passage
 * highlighted on the left, and the results on the right — grouped by
 * severity, rule or document, each with its passage, why it falls short, the
 * rule it is measured against, and what to do: Accept, Raise as finding,
 * Ask Amoreg.
 *
 * While the run works, results arrive as the agent reports them (the
 * compliance stream re-reads the review, with a slow timer behind it) and the
 * top shows what it is reading. A run that failed says why, beside Retry.
 * `?gap=` selects a gap, so a raised finding's source link lands on it.
 *
 * @module components/apps/contractIntelligence/CiReviewPage
 */
import { Query } from "@lucasmarkes/hairline/react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ArrowUpRightIcon,
  CheckIcon,
  FileSearchIcon,
  FileTextIcon,
  Loader2Icon,
  MessageSquareIcon,
  RotateCcwIcon,
  RotateCwIcon,
  SendIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { useStartChatWithText } from "../../../hooks/useStartChatWithText";
import { cn } from "../../../lib/utils";
import {
  decideCiGap,
  deleteCiReview,
  raiseCiGap,
  runCiReview,
  useCiReview,
  useCiSettings,
  type CiDocument,
  type CiGap,
  type CiReviewDetail,
  type CiSeverity,
} from "../../../state/contractIntelligence";
import { policyDisplayName, usePolicies } from "../../../state/policies";
import { SegmentedTabs } from "../../regulation/PageHeader";
import { SeverityGlyph } from "../../regulation/finding/findingGlyphs";
import { Button } from "../../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { toastManager } from "../../ui/toast";
import {
  askAmoregPrompt,
  groupGaps,
  highlightRuns,
  isActive,
  readableName,
  reviewProgress,
  SEVERITY_LABEL,
  SEVERITY_ORDER,
  shortDate,
  type GapGrouping,
} from "./ciModel";
import { CiConfirmDialog, CiEmpty, CiPage, CiUnavailable, ReviewStatusMark } from "./ciParts";
import { BasisChip } from "./CiReviewRow";
import { useCiOrg } from "./useCiOrg";

/* ------------------------------------------------------------------ */
/* Severity ink for passages                                           */
/* ------------------------------------------------------------------ */

const MARK: Readonly<Record<CiSeverity, { readonly idle: string; readonly active: string }>> = {
  critical: {
    idle: "bg-red-500/12 decoration-red-500/70 dark:bg-red-400/15",
    active: "bg-red-500/25 ring-red-500/60 dark:bg-red-400/30",
  },
  high: {
    idle: "bg-orange-500/12 decoration-orange-500/70 dark:bg-orange-400/15",
    active: "bg-orange-500/25 ring-orange-500/60 dark:bg-orange-400/30",
  },
  medium: {
    idle: "bg-amber-400/15 decoration-amber-500/70 dark:bg-amber-300/15",
    active: "bg-amber-400/30 ring-amber-500/60 dark:bg-amber-300/30",
  },
  low: {
    idle: "bg-sky-500/10 decoration-sky-500/60 dark:bg-sky-400/12",
    active: "bg-sky-500/22 ring-sky-500/60 dark:bg-sky-400/28",
  },
};

/* ------------------------------------------------------------------ */
/* Summary                                                             */
/* ------------------------------------------------------------------ */

function SummaryBand({
  detail,
  onRun,
  running,
}: {
  readonly detail: CiReviewDetail;
  readonly onRun: () => void;
  readonly running: boolean;
}) {
  const { review, documents, gaps } = detail;
  const [expanded, setExpanded] = useState(false);
  const counts = useMemo(() => {
    const open: Record<CiSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    let accepted = 0;
    let raised = 0;
    for (const gap of gaps) {
      if (gap.status === "open") open[gap.severity] += 1;
      else if (gap.status === "accepted") accepted += 1;
      else raised += 1;
    }
    return { open, accepted, raised };
  }, [gaps]);
  const progress = reviewProgress(review, documents, gaps.length);
  const meta = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
      <BasisChip basis={review.basis} />
      <span>
        {documents.length} document{documents.length === 1 ? "" : "s"}
      </span>
      {review.createdByEmail ? <span>Started by {review.createdByEmail}</span> : null}
      <span>{shortDate(review.startedAt ?? review.createdAt)}</span>
    </div>
  );

  if (isActive(review.status)) {
    return (
      <div className="space-y-3 border-b border-border/60 px-4 py-4 sm:px-6" data-ci-summary="running" aria-live="polite">
        <div className="flex items-center gap-2 text-[13px]">
          <Loader2Icon aria-hidden className="size-3.5 animate-spin text-primary motion-reduce:animate-none" />
          <span className="font-medium">{progress.phase}</span>
          <span className="text-muted-foreground">· {gaps.length} gap{gaps.length === 1 ? "" : "s"} found so far</span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress.fraction * 100)} aria-label="Review progress">
          <div className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out" style={{ width: `${Math.max(4, Math.round(progress.fraction * 100))}%` }} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {meta}
          <span className="text-xs text-muted-foreground">Results appear as they are found. You can leave this page.</span>
        </div>
      </div>
    );
  }

  if (review.status === "failed" || review.status === "draft") {
    return (
      <div className="space-y-3 border-b border-border/60 px-4 py-4 sm:px-6" data-ci-summary={review.status}>
        <div
          className={cn(
            "flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center",
            review.status === "failed" ? "border-destructive/30 bg-destructive/5" : "border-border/70 bg-muted/20",
          )}
          role={review.status === "failed" ? "alert" : undefined}
        >
          {review.status === "failed" ? <AlertTriangleIcon aria-hidden className="size-4 shrink-0 text-destructive" /> : null}
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium">{review.status === "failed" ? "The review did not finish" : "This review has not run yet"}</p>
            <p className="text-xs text-muted-foreground">
              {review.status === "failed"
                ? `${review.error ?? "It stopped before reporting its results."}${gaps.length > 0 ? ` The ${gaps.length} gap${gaps.length === 1 ? "" : "s"} it found before stopping are shown below.` : ""}`
                : "Run it to check the documents against the basis."}
            </p>
          </div>
          <Button size="sm" className="gap-1.5 self-start sm:self-auto" onClick={onRun} disabled={running}>
            {running ? <Loader2Icon aria-hidden className="size-3.5 animate-spin" /> : <RotateCwIcon aria-hidden className="size-3.5" />}
            {review.status === "failed" ? "Retry" : "Run review"}
          </Button>
        </div>
        {meta}
      </div>
    );
  }

  const summary = review.summary ?? "";
  const long = summary.length > 320;
  return (
    <div className="space-y-3 border-b border-border/60 px-4 py-4 sm:px-6" data-ci-summary="completed">
      <div className="flex flex-wrap items-center gap-2">
        {review.conclusion === "no_gaps" ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-700 dark:text-emerald-400">
            <CheckIcon aria-hidden className="size-4" />
            No gaps found
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {SEVERITY_ORDER.map((severity) => (
              <span
                key={severity}
                className={cn(
                  "inline-flex h-6 items-center gap-1 rounded-md border border-border/70 px-1.5 text-xs tabular-nums",
                  counts.open[severity] === 0 && "opacity-50",
                )}
              >
                <SeverityGlyph severity={severity} className="size-3.5" />
                {counts.open[severity]} {SEVERITY_LABEL[severity].toLowerCase()}
              </span>
            ))}
            <span className="ms-1 text-xs text-muted-foreground">
              open · {counts.accepted} accepted · {counts.raised} raised
            </span>
          </div>
        )}
      </div>
      {summary ? (
        <div className="max-w-[80ch]">
          <p className={cn("whitespace-pre-line text-[13px] leading-6 text-foreground/90", !expanded && long && "line-clamp-3")}>{summary}</p>
          {long ? (
            <button type="button" className="mt-0.5 rounded text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setExpanded(!expanded)}>
              {expanded ? "Show less" : "Show more"}
            </button>
          ) : null}
        </div>
      ) : null}
      {meta}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Document viewer                                                     */
/* ------------------------------------------------------------------ */

function DocumentViewer({
  document,
  gaps,
  selectedGapId,
  onSelect,
}: {
  readonly document: CiDocument;
  readonly gaps: ReadonlyArray<CiGap>;
  readonly selectedGapId: string | null;
  readonly onSelect: (gapId: string) => void;
}) {
  const runs = useMemo(() => highlightRuns(document.text, gaps), [document.text, gaps]);
  const titles = useMemo(() => new Map(gaps.map((gap) => [gap.id, gap.title])), [gaps]);
  const decided = useMemo(() => new Set(gaps.filter((gap) => gap.status !== "open").map((gap) => gap.id)), [gaps]);
  return (
    <article className="mx-auto w-full max-w-[74ch] px-5 py-6 sm:px-8" aria-label={document.name} data-ci-viewer={document.id}>
      <header className="mb-5 space-y-1 border-b border-border/50 pb-4">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <FileTextIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate">{document.name}</span>
        </h2>
        <p className="text-xs text-muted-foreground">
          {[document.documentType, document.counterparty].filter(Boolean).join(" · ") || "Counterparty not named"}
        </p>
        {document.summary ? <p className="text-xs text-muted-foreground">{document.summary}</p> : null}
      </header>
      <div className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.75] text-foreground/90">
        {runs.map((run) => {
          if (run.gapIds.length === 0 || !run.severity) return <span key={run.start}>{run.text}</span>;
          const active = selectedGapId !== null && run.gapIds.includes(selectedGapId);
          const primary = active ? selectedGapId! : run.gapIds[0]!;
          const quiet = run.gapIds.every((id) => decided.has(id));
          return (
            <mark
              key={run.start}
              role="button"
              tabIndex={0}
              aria-label={`Flagged: ${titles.get(primary) ?? "gap"}`}
              aria-pressed={active}
              data-ci-mark={primary}
              data-ci-mark-active={active ? "" : undefined}
              onClick={() => onSelect(primary)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(primary);
                }
              }}
              className={cn(
                "cursor-pointer rounded-[3px] text-inherit underline decoration-2 underline-offset-[5px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                active ? cn(MARK[run.severity].active, "ring-1") : MARK[run.severity].idle,
                quiet && !active && "bg-transparent decoration-dotted opacity-80 dark:bg-transparent",
              )}
            >
              {run.text}
            </mark>
          );
        })}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Raise as finding                                                    */
/* ------------------------------------------------------------------ */

function RaiseDialog({
  organizationId,
  reviewId,
  gap,
  onClose,
}: {
  readonly organizationId: string;
  readonly reviewId: string;
  readonly gap: CiGap;
  readonly onClose: () => void;
}) {
  const policies = usePolicies(organizationId);
  const choices = useMemo(
    () => (policies.snapshot?.policies ?? []).filter((policy) => policy.status === "ready" && policy.reference !== true),
    [policies.snapshot],
  );
  const [policyId, setPolicyId] = useState<string | null>(gap.rule.kind === "policies" ? gap.rule.reference : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = choices.find((policy) => policy.id === policyId) ?? null;
  const raise = async () => {
    if (!policyId) return;
    setBusy(true);
    setError(null);
    const outcome = await raiseCiGap(organizationId, reviewId, gap.id, policyId);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    toastManager.add({ type: "success", title: "Raised as a finding", description: chosen ? `On ${readableName(policyDisplayName(chosen))} in Governance.` : "In Governance." });
    onClose();
  };
  return (
    <Dialog open onOpenChange={(open) => (!open && !busy ? onClose() : undefined)}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Raise as finding</DialogTitle>
          <DialogDescription>
            Creates a finding in Governance on the policy you choose, with this gap's severity, passage and suggested fix. The finding links back
            here.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <div className="rounded-lg border border-border/70 bg-muted/20 px-3 py-2">
            <p className="flex items-center gap-1.5 text-[13px] font-medium">
              <SeverityGlyph severity={gap.severity} />
              {gap.title}
            </p>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">The policy this finding is about</span>
            <Select value={policyId ?? ""} onValueChange={(next) => setPolicyId(next || null)}>
              <SelectTrigger aria-label="Policy" className="w-full">
                <span className="truncate">{chosen ? readableName(policyDisplayName(chosen)) : policies.snapshot ? "Choose a policy" : "Loading your library…"}</span>
              </SelectTrigger>
              <SelectContent>
                {choices.map((policy) => (
                  <SelectItem key={policy.id} value={policy.id}>
                    {readableName(policyDisplayName(policy))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" className="gap-1.5" disabled={!policyId || busy} onClick={() => void raise()}>
            {busy ? <Loader2Icon aria-hidden className="size-3.5 animate-spin" /> : <SendIcon aria-hidden className="size-3.5" />}
            Raise finding
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Results                                                             */
/* ------------------------------------------------------------------ */

function GapCard({
  gap,
  documentName,
  selected,
  canManage,
  requireNote,
  orgSegment,
  onSelect,
  onShowInDocument,
  onAccept,
  onReopen,
  onRaise,
  onAsk,
}: {
  readonly gap: CiGap;
  readonly documentName: string | null;
  readonly selected: boolean;
  readonly canManage: boolean;
  readonly requireNote: boolean;
  readonly orgSegment: string;
  readonly onSelect: () => void;
  readonly onShowInDocument: () => void;
  readonly onAccept: (note: string | null) => Promise<boolean>;
  readonly onReopen: () => void;
  readonly onRaise: () => void;
  readonly onAsk: (() => void) | null;
}) {
  const [accepting, setAccepting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!selected) setAccepting(false);
  }, [selected]);
  const confirmAccept = async () => {
    setBusy(true);
    const ok = await onAccept(note.trim() || null);
    setBusy(false);
    if (ok) {
      setAccepting(false);
      setNote("");
    }
  };
  return (
    <li
      className={cn(
        // Clear of the sticky group heading when scrolled into view.
        "scroll-mt-9 border-b border-border/50 transition-colors",
        selected ? "bg-accent/35" : "hover:bg-accent/20",
      )}
      data-ci-gap={gap.id}
      data-ci-gap-status={gap.status}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-expanded={selected}
        className="flex w-full items-start gap-2.5 px-4 py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <SeverityGlyph severity={gap.severity} className="mt-0.5" />
        <span className="min-w-0 flex-1">
          <span className={cn("block text-[13px] font-medium leading-5", gap.status !== "open" && "text-muted-foreground")}>{gap.title}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {gap.rule.label}
            {documentName ? ` · ${documentName}` : ""}
          </span>
        </span>
        {gap.status !== "open" ? (
          <span
            className={cn(
              "mt-0.5 inline-flex h-5 shrink-0 items-center gap-1 rounded-md border px-1.5 text-[11px]",
              gap.status === "raised" ? "border-primary/30 text-primary" : "border-border/70 text-muted-foreground",
            )}
          >
            {gap.status === "raised" ? <SendIcon aria-hidden className="size-3" /> : <CheckIcon aria-hidden className="size-3" />}
            {gap.status === "raised" ? "Raised" : "Accepted"}
          </span>
        ) : null}
      </button>
      {selected ? (
        <div className="space-y-3 px-4 pb-4 ps-[2.6rem]">
          <figure className="space-y-1">
            <blockquote className="border-s-2 border-border ps-3 text-[13px] leading-6 text-foreground/85">“{gap.passage}”</blockquote>
            <figcaption>
              <button type="button" onClick={onShowInDocument} className="inline-flex items-center gap-1 rounded text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
                <FileSearchIcon aria-hidden className="size-3" />
                Show in document
              </button>
            </figcaption>
          </figure>
          <div className="space-y-1">
            <h4 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Why it falls short</h4>
            <p className="text-[13px] leading-6">{gap.explanation}</p>
          </div>
          <div className="space-y-1 rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
            <h4 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Measured against</h4>
            <p className="text-[13px] font-medium">{gap.rule.label}</p>
            {gap.rule.quote ? <p className="text-xs leading-5 text-muted-foreground">“{gap.rule.quote}”</p> : null}
          </div>
          {gap.recommendation ? (
            <div className="space-y-1">
              <h4 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Suggested fix</h4>
              <p className="text-[13px] leading-6">{gap.recommendation}</p>
            </div>
          ) : null}
          {gap.status === "accepted" ? (
            <p className="text-xs text-muted-foreground">
              Accepted{gap.decidedByEmail ? ` by ${gap.decidedByEmail}` : ""}
              {gap.decidedAt ? ` on ${shortDate(gap.decidedAt)}` : ""}
              {gap.decisionNote ? `: “${gap.decisionNote}”` : "."}
            </p>
          ) : null}
          {gap.status === "raised" ? (
            <p className="text-xs text-muted-foreground">
              Raised{gap.decidedByEmail ? ` by ${gap.decidedByEmail}` : ""}
              {gap.decidedAt ? ` on ${shortDate(gap.decidedAt)}` : ""}.{" "}
              {gap.findingId ? (
                <Link
                  to="/$org/compliance/findings/$findingId"
                  params={{ org: orgSegment, findingId: gap.findingId }}
                  className="inline-flex items-center gap-0.5 font-medium text-foreground underline-offset-2 hover:underline"
                >
                  Open the finding
                  <ArrowUpRightIcon aria-hidden className="size-3" />
                </Link>
              ) : null}
            </p>
          ) : null}
          {accepting ? (
            <div className="space-y-2">
              <Textarea
                autoFocus
                aria-label="Why this gap is accepted"
                value={note}
                onChange={(event) => setNote(event.currentTarget.value)}
                placeholder={requireNote ? "Why is this acceptable? (required)" : "Why is this acceptable? (optional)"}
                className="min-h-16 text-[13px]"
              />
              <div className="flex gap-1.5">
                <Button size="xs" onClick={() => void confirmAccept()} disabled={busy || (requireNote && note.trim().length === 0)}>
                  {busy ? "Saving…" : "Accept gap"}
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setAccepting(false)} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {gap.status === "open" ? (
                <>
                  <Button size="xs" variant="outline" className="gap-1" onClick={() => setAccepting(true)}>
                    <CheckIcon aria-hidden className="size-3" />
                    Accept
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    className="gap-1"
                    onClick={onRaise}
                    disabled={!canManage}
                    title={canManage ? undefined : "Only organization admins can raise findings"}
                  >
                    <SendIcon aria-hidden className="size-3" />
                    Raise as finding
                  </Button>
                </>
              ) : gap.status === "accepted" ? (
                <Button size="xs" variant="outline" className="gap-1" onClick={onReopen}>
                  <RotateCcwIcon aria-hidden className="size-3" />
                  Reopen
                </Button>
              ) : null}
              {onAsk ? (
                <Button size="xs" variant="ghost" className="gap-1" onClick={onAsk}>
                  <MessageSquareIcon aria-hidden className="size-3" />
                  Ask Amoreg
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

type GapFilter = "open" | "all";

function ResultsPanel({
  detail,
  organizationId,
  orgSegment,
  canManage,
  requireNote,
  selectedGapId,
  onSelect,
  onShowInDocument,
}: {
  readonly detail: CiReviewDetail;
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly canManage: boolean;
  readonly requireNote: boolean;
  readonly selectedGapId: string | null;
  readonly onSelect: (gapId: string | null) => void;
  readonly onShowInDocument: (gap: CiGap) => void;
}) {
  const { review, documents, gaps } = detail;
  const [grouping, setGrouping] = useState<GapGrouping>("severity");
  const [filter, setFilter] = useState<GapFilter>("open");
  const [raising, setRaising] = useState<CiGap | null>(null);
  const startChat = useStartChatWithText(organizationId);
  const openCount = gaps.filter((gap) => gap.status === "open").length;
  // A selected gap stays visible even when the filter would hide it (a deep link to a decided gap).
  const shown = gaps.filter((gap) => filter === "all" || gap.status === "open" || gap.id === selectedGapId);
  const groups = useMemo(() => groupGaps(shown, grouping, documents), [documents, grouping, shown]);
  const ordered = groups.flatMap((group) => group.gaps);
  const documentName = (gap: CiGap) => (documents.length > 1 ? (documents.find((document) => document.id === gap.documentId)?.name ?? null) : null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedGapId) return;
    listRef.current?.querySelector(`[data-ci-gap="${CSS.escape(selectedGapId)}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedGapId]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "j" && event.key !== "k") return;
    if ((event.target as HTMLElement).closest("textarea,input")) return;
    event.preventDefault();
    const index = ordered.findIndex((gap) => gap.id === selectedGapId);
    const step = event.key === "ArrowDown" || event.key === "j" ? 1 : -1;
    const next = ordered[Math.max(0, Math.min(ordered.length - 1, index + step))];
    if (next) onSelect(next.id);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-ci-results="">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border/60 px-4 py-2">
        <SegmentedTabs<GapFilter>
          label="Show"
          value={filter}
          onValueChange={setFilter}
          tabs={[
            { value: "open", label: "Open", count: openCount },
            { value: "all", label: "All", count: gaps.length },
          ]}
        />
        <span className="flex-1" />
        <Select value={grouping} onValueChange={(next) => setGrouping((next ?? "severity") as GapGrouping)}>
          <SelectTrigger size="sm" className="w-36" aria-label="Group by">
            <span className="text-xs">
              Group: {grouping === "severity" ? "Severity" : grouping === "rule" ? "Rule" : "Document"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="severity">Severity</SelectItem>
            <SelectItem value="rule">Rule</SelectItem>
            {documents.length > 1 ? <SelectItem value="document">Document</SelectItem> : null}
          </SelectContent>
        </Select>
      </div>
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto" onKeyDown={onKeyDown} role="region" aria-label="Results">
        {gaps.length === 0 ? (
          isActive(review.status) ? (
            <div className="space-y-3 p-4" aria-busy="true">
              {[0, 1, 2].map((index) => (
                <div key={index} className="space-y-2 rounded-lg border border-border/50 p-3">
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted/60" />
                  <div className="h-2.5 w-1/2 animate-pulse rounded bg-muted/40" />
                </div>
              ))}
              <p className="text-center text-xs text-muted-foreground">Gaps appear here as they are found.</p>
            </div>
          ) : review.status === "completed" ? (
            <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
              <CheckIcon aria-hidden className="size-5 text-emerald-600 dark:text-emerald-400" />
              <p className="text-sm font-medium">No gaps</p>
              <p className="text-xs text-muted-foreground">Every clause meets what it was checked against.</p>
            </div>
          ) : (
            <p className="px-6 py-14 text-center text-xs text-muted-foreground">No results yet.</p>
          )
        ) : ordered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <CheckIcon aria-hidden className="size-5 text-muted-foreground" />
            <p className="text-sm font-medium">Every gap is decided</p>
            <Button size="xs" variant="ghost" onClick={() => setFilter("all")}>
              Show all {gaps.length}
            </Button>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.key} aria-label={group.label}>
              <h3 className="sticky top-0 z-10 flex h-8 items-center gap-2 border-b border-border/50 bg-background/95 px-4 text-xs font-medium text-muted-foreground backdrop-blur">
                {group.severity ? <SeverityGlyph severity={group.severity} className="size-3.5" /> : null}
                <span className="truncate">{group.label}</span>
                <span className="tabular-nums text-muted-foreground/70">{group.gaps.length}</span>
              </h3>
              <ul>
                {group.gaps.map((gap) => (
                  <GapCard
                    key={gap.id}
                    gap={gap}
                    documentName={documentName(gap)}
                    selected={gap.id === selectedGapId}
                    canManage={canManage}
                    requireNote={requireNote}
                    orgSegment={orgSegment}
                    onSelect={() => onSelect(gap.id === selectedGapId ? null : gap.id)}
                    onShowInDocument={() => onShowInDocument(gap)}
                    onAccept={async (note) => {
                      const outcome = await decideCiGap(organizationId, review.id, gap.id, "accept", note);
                      if (!outcome.ok) toastManager.add({ type: "error", title: "Not accepted", description: outcome.message });
                      return outcome.ok;
                    }}
                    onReopen={() =>
                      void decideCiGap(organizationId, review.id, gap.id, "reopen", null).then((outcome) => {
                        if (!outcome.ok) toastManager.add({ type: "error", title: "Not reopened", description: outcome.message });
                      })
                    }
                    onRaise={() => setRaising(gap)}
                    onAsk={
                      startChat
                        ? () =>
                            void startChat(
                              askAmoregPrompt({
                                reviewTitle: review.title,
                                documentName: documents.find((document) => document.id === gap.documentId)?.name ?? "the document",
                                gap,
                              }),
                            )
                        : null
                    }
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
      {raising ? <RaiseDialog organizationId={organizationId} reviewId={review.id} gap={raising} onClose={() => setRaising(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type Pane = "results" | "document";

export function CiReviewPage({
  reviewId,
  selectedGapId,
  onSelectGap,
}: {
  readonly reviewId: string;
  readonly selectedGapId: string | null;
  readonly onSelectGap: (gapId: string | null) => void;
}) {
  const { organizationId, orgSegment, canManage } = useCiOrg();
  const view = useCiReview(organizationId, reviewId);
  const settings = useCiSettings(organizationId);
  const navigate = useNavigate();
  const detail = view.read?.kind === "ready" ? view.read.value : null;
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [pane, setPane] = useState<Pane>("results");
  const [starting, setStarting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  const documentId = activeDocumentId ?? detail?.documents[0]?.id ?? null;
  const document = detail?.documents.find((candidate) => candidate.id === documentId) ?? detail?.documents[0] ?? null;
  const selectedGap = detail?.gaps.find((gap) => gap.id === selectedGapId) ?? null;

  const scrollToMark = useCallback((gapId: string) => {
    window.requestAnimationFrame(() => {
      viewerRef.current
        ?.querySelector(`[data-ci-mark="${CSS.escape(gapId)}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }, []);

  // Selecting a gap shows its document and brings its passage into view.
  useEffect(() => {
    if (!selectedGap) return;
    if (selectedGap.documentId !== documentId) setActiveDocumentId(selectedGap.documentId);
    scrollToMark(selectedGap.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGap?.id]);

  const run = async () => {
    if (!organizationId) return;
    setStarting(true);
    const outcome = await runCiReview(organizationId, reviewId);
    setStarting(false);
    if (!outcome.ok) toastManager.add({ type: "error", title: "The review did not start", description: outcome.message });
  };

  if (view.read === null) {
    return (
      <CiPage title="Review" data-ci-page="review">
        <div className="space-y-3 px-6 py-6" aria-busy="true" aria-label="Loading the review">
          <div className="h-4 w-1/3 animate-pulse rounded bg-muted/60" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted/40" />
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_400px]">
            <div className="space-y-2">
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="h-3 animate-pulse rounded bg-muted/40" style={{ width: `${70 + ((index * 13) % 30)}%` }} />
              ))}
            </div>
            <div className="hidden space-y-2 lg:block">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="h-14 animate-pulse rounded-lg bg-muted/40" />
              ))}
            </div>
          </div>
        </div>
      </CiPage>
    );
  }
  if (view.read.kind === "missing") {
    return (
      <CiPage title="Review" data-ci-page="review">
        <CiEmpty title="Review not found" description="It is not in this organization's reviews, or it was deleted." art={Query}>
          {orgSegment ? (
            <Button size="sm" variant="outline" render={<Link to="/$org/apps/contract-intelligence/reviews" params={{ org: orgSegment }} />}>
              All reviews
            </Button>
          ) : null}
        </CiEmpty>
      </CiPage>
    );
  }
  if (!detail || !organizationId || !orgSegment || !document) {
    return (
      <CiPage title="Review" data-ci-page="review">
        <CiUnavailable what="The review" onRetry={view.reload} />
      </CiPage>
    );
  }

  const { review } = detail;
  const requireNote = settings.read?.kind === "ready" ? settings.read.value.requireAcceptNote : false;
  const documentGaps = detail.gaps.filter((gap) => gap.documentId === document.id);
  const canRerun = !isActive(review.status) && review.status !== "draft" && review.status !== "failed";

  return (
    <CiPage
      title={review.title}
      meta={<ReviewStatusMark status={review.status} withLabel />}
      data-ci-page="review"
      bodyClassName="flex flex-col overflow-hidden"
      actions={
        <>
          {canRerun ? (
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => void run()} disabled={starting} title="Run the review again; decisions on the same gaps are kept">
              {starting ? <Loader2Icon aria-hidden className="size-3.5 animate-spin" /> : <RotateCwIcon aria-hidden className="size-3.5" />}
              <span className="hidden sm:inline">Run again</span>
            </Button>
          ) : null}
          {!isActive(review.status) ? (
            <Button size="icon-sm" variant="ghost" aria-label="Delete review" onClick={() => setDeleting(true)}>
              <Trash2Icon aria-hidden />
            </Button>
          ) : null}
        </>
      }
    >
      <SummaryBand detail={detail} onRun={() => void run()} running={starting} />
      <div className="flex shrink-0 border-b border-border/60 px-4 py-2 lg:hidden">
        <SegmentedTabs<Pane>
          label="View"
          value={pane}
          onValueChange={setPane}
          tabs={[
            { value: "results", label: "Results", count: detail.gaps.length },
            { value: "document", label: "Document" },
          ]}
        />
      </div>
      <div className="flex min-h-0 flex-1">
        <div className={cn("min-h-0 min-w-0 flex-1 flex-col", pane === "document" ? "flex" : "hidden lg:flex")}>
          {detail.documents.length > 1 ? (
            <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border/60 px-4 py-2" role="tablist" aria-label="Documents">
              {detail.documents.map((candidate) => {
                const count = detail.gaps.filter((gap) => gap.documentId === candidate.id && gap.status === "open").length;
                const active = candidate.id === document.id;
                return (
                  <button
                    key={candidate.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveDocumentId(candidate.id)}
                    className={cn(
                      "flex h-7 max-w-64 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                      active ? "bg-accent font-medium text-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                    )}
                  >
                    <FileTextIcon aria-hidden className="size-3.5 shrink-0" />
                    <span className="truncate">{candidate.name}</span>
                    {count > 0 ? <span className="text-[11px] tabular-nums text-muted-foreground">{count}</span> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
          <div ref={viewerRef} className="min-h-0 flex-1 overflow-y-auto bg-muted/10">
            <DocumentViewer
              document={document}
              gaps={documentGaps}
              selectedGapId={selectedGapId}
              onSelect={(gapId) => {
                onSelectGap(gapId);
                setPane("results");
              }}
            />
          </div>
        </div>
        <aside
          className={cn(
            "min-h-0 w-full flex-col border-border/60 lg:w-[420px] lg:shrink-0 lg:border-s xl:w-[460px]",
            pane === "results" ? "flex" : "hidden lg:flex",
          )}
          aria-label="Review results"
        >
          <ResultsPanel
            detail={detail}
            organizationId={organizationId}
            orgSegment={orgSegment}
            canManage={canManage}
            requireNote={requireNote}
            selectedGapId={selectedGapId}
            onSelect={onSelectGap}
            onShowInDocument={(gap) => {
              setActiveDocumentId(gap.documentId);
              setPane("document");
              scrollToMark(gap.id);
            }}
          />
        </aside>
      </div>
      {deleting ? (
        <CiConfirmDialog
          title="Delete this review?"
          description={<>“{review.title}”, its documents and its results are removed for everyone. Findings already raised in Governance stay there.</>}
          confirmLabel="Delete review"
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            const outcome = await deleteCiReview(organizationId, review.id);
            if (!outcome.ok) {
              toastManager.add({ type: "error", title: "Not deleted", description: outcome.message });
              return;
            }
            setDeleting(false);
            toastManager.add({ type: "success", title: "Review deleted" });
            void navigate({ to: "/$org/apps/contract-intelligence/reviews", params: { org: orgSegment } });
          }}
        />
      ) : null}
    </CiPage>
  );
}
